import type { FC, FormEvent, ReactNode } from 'react'

import Link from 'next/link'
import { useRouter } from 'next/router'
import { useEffect, useState } from 'react'
import { Eye, EyeOff } from 'lucide-react'
import { cn } from '@/lib/utils'
import { Seo } from '@/components/generals/seo'
import { Layout } from '@/components/generals/layout'
import { Landscape } from '@/components/generals/landscape'
import { LogoMark } from '@/components/generals/logo'
import { PillButton } from '@/components/generals/pill-button'
import { useAuth } from '@/components/generals/auth-provider'
import { LANDSCAPES } from '@/lib/visual'

import {
    Field,
    FormMessage,
    TextInput
} from '@/components/generals/form-fields'

type Mode = 'signin' | 'signup' | 'forgot' | 'reset'

const PLANS = ['Free', 'Plus', 'Private']

const TITLES: Record<Mode, { title: string, subtitle: string }> = {
    signin: { title: 'Nice to see you again', subtitle: 'Sign in to pick up where you left off.' },
    signup: { title: 'Begin with Verdane', subtitle: 'Free to begin, and no card required.' },
    forgot: { title: 'Recover your password', subtitle: 'We will send a six-digit code to your inbox.' },
    reset: { title: 'Set a fresh password', subtitle: 'Type the code we emailed you, then pick a new password.' }
}

const safeNext = (value: unknown): string => {
    if (typeof value !== 'string') return '/account'

    return value.startsWith('/') && !value.startsWith('//') ? value : '/account'
}

const LoginPage: FC = (): ReactNode => {
    const router = useRouter()
    const { member, ready, setMember, signOut } = useAuth()
    const [mode, setMode] = useState<Mode>('signin')
    const [plan, setPlan] = useState('Free')
    const [name, setName] = useState('')
    const [email, setEmail] = useState('')
    const [password, setPassword] = useState('')
    const [code, setCode] = useState('')
    const [showPassword, setShowPassword] = useState(false)
    const [busy, setBusy] = useState(false)
    const [error, setError] = useState<string | null>(null)
    const [notice, setNotice] = useState<string | null>(null)

    useEffect(() => {
        if (!router.isReady) return

        const { mode: requestedMode, plan: requestedPlan } = router.query

        if (requestedMode === 'signup') setMode('signup')
        if (typeof requestedPlan === 'string' && PLANS.includes(requestedPlan)) setPlan(requestedPlan)
    }, [router.isReady, router.query])

    const switchMode = (next: Mode) => {
        setMode(next)
        setError(null)
        setNotice(null)
        setPassword('')
        setCode('')
    }

    const request = async (url: string, payload: Record<string, string>) => {
        const response = await fetch(url, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(payload)
        })
        const data = await response.json().catch(() => ({}))

        return { ok: response.ok, data }
    }

    const submit = async (event: FormEvent<HTMLFormElement>) => {
        event.preventDefault()
        setError(null)
        setNotice(null)
        setBusy(true)

        try {
            if (mode === 'signin' || mode === 'signup') {
                const { ok, data } = mode === 'signin'
                    ? await request('/api/auth/login', { email, password })
                    : await request('/api/auth/signup', { name, email, password, plan })

                if (!ok) {
                    setError(data?.message ?? 'That did not work. Please try once more.')
                    return
                }

                setMember(data.member)
                router.push(safeNext(router.query.next))
                return
            }

            if (mode === 'forgot') {
                const { ok, data } = await request('/api/auth/forgot', { email })

                if (!ok) {
                    setError(data?.message ?? 'That did not work. Please try once more.')
                    return
                }

                setMode('reset')
                setNotice(data?.message ?? 'If that email belongs to an account, a six-digit code is heading your way.')
                return
            }

            const { ok, data } = await request('/api/auth/reset', { email, code, password })

            if (!ok) {
                setError(data?.message ?? 'That did not work. Please try once more.')
                return
            }

            setMember(data.member)
            router.push(safeNext(router.query.next))
        }
        catch {
            setError('We could not connect just now. Check your internet and try again.')
        }
        finally {
            setBusy(false)
        }
    }

    const copy = TITLES[mode]
    const submitLabel = {
        signin: 'Sign in',
        signup: plan === 'Free' ? 'Open my account' : `Start my free ${plan} trial`,
        forgot: 'Send my code',
        reset: 'Save my new password'
    }[mode]

    return (
        <Layout>
            <Seo
                title={mode === 'signup' ? 'Open an account' : 'Sign in'}
                description='Sign in to Verdane, or open a free account to talk with your AI advisor and keep track of your calls with advisors.'
            />

            <section aria-labelledby='login-title' className='relative isolate flex min-h-[100svh] items-center justify-center overflow-hidden px-4 pb-20 pt-32'>
                <div aria-hidden='true' className='absolute inset-0 -z-10'>
                    <Landscape src={LANDSCAPES.heroHills} priority fade='none' position='center 60%' />
                    <div className='absolute inset-0 bg-black/60' />
                    <div className='absolute inset-0 bg-[radial-gradient(60%_55%_at_50%_45%,rgba(0,0,0,0)_0%,rgba(0,0,0,0.75)_100%)]' />
                    <div className='absolute inset-x-0 bottom-0 h-40 bg-gradient-to-b from-transparent to-black' />
                </div>

                <div className='glass-dark grain motion-rise relative w-full max-w-md overflow-hidden rounded-[32px] p-6 sm:p-9 [--rise-delay:150ms]'>
                    <div className='flex flex-col items-center text-center'>
                        <span className='grid size-12 place-items-center rounded-full bg-white/[0.06] ring-1 ring-white/10'>
                            <LogoMark className='size-6 text-cream' />
                        </span>

                        <h1 id='login-title' className='mt-6 text-[2rem] font-normal leading-tight tracking-[-0.04em] text-cream'>
                            {copy.title.split(' ').slice(0, -1).join(' ')} <span className='em-serif'>{copy.title.split(' ').slice(-1)}</span>
                        </h1>

                        <p className='mt-2 text-sm text-cream/55'>
                            {copy.subtitle}
                        </p>
                    </div>

                    {ready && member
                        ? (
                            <div className='mt-8 text-center'>
                                <p className='text-[15px] text-cream/80'>
                                    You are already signed in as <span className='text-cream'>{member.name}</span>.
                                </p>

                                <div className='mt-6 flex flex-col items-center gap-4'>
                                    <PillButton href='/account'>
                                        Open your account
                                    </PillButton>

                                    <button type='button' onClick={() => signOut()} className='text-sm text-cream/60 underline decoration-cream/30 underline-offset-[6px] hover:text-cream'>
                                        Sign out
                                    </button>
                                </div>
                            </div>
                        )
                        : (
                            <>
                                {(mode === 'signin' || mode === 'signup') && (
                                    <div role='tablist' aria-label='Account' className='mt-8 grid grid-cols-2 rounded-full bg-white/[0.05] p-1 ring-1 ring-white/10'>
                                        {(['signin', 'signup'] as Array<Mode>).map(option => (
                                            <button
                                                key={option}
                                                type='button'
                                                role='tab'
                                                aria-selected={mode === option}
                                                onClick={() => switchMode(option)}
                                                className={cn('h-10 rounded-full text-sm transition-colors duration-300', mode === option ? 'bg-cream text-black' : 'text-cream/65 hover:text-cream')}
                                            >
                                                {option === 'signin' ? 'Sign in' : 'New account'}
                                            </button>
                                        ))}
                                    </div>
                                )}

                                <form onSubmit={submit} noValidate className='mt-7 space-y-4'>
                                    {mode === 'signup' && (
                                        <Field id='auth-name' label='Full name'>
                                            <TextInput id='auth-name' name='name' autoComplete='name' required maxLength={80} value={name} onChange={event => setName(event.target.value)} />
                                        </Field>
                                    )}

                                    <Field id='auth-email' label='Email address'>
                                        <TextInput id='auth-email' name='email' type='email' autoComplete='email' required value={email} onChange={event => setEmail(event.target.value)} />
                                    </Field>

                                    {mode === 'reset' && (
                                        <Field id='auth-code' label='Code from your email'>
                                            <TextInput
                                                id='auth-code'
                                                name='code'
                                                inputMode='numeric'
                                                autoComplete='one-time-code'
                                                maxLength={6}
                                                required
                                                value={code}
                                                onChange={event => setCode(event.target.value.replace(/\D/g, ''))}
                                                className='text-center text-xl tracking-[0.5em]'
                                            />
                                        </Field>
                                    )}

                                    {mode !== 'forgot' && (
                                        <Field id='auth-password' label={mode === 'reset' ? 'New password' : 'Password'} hint={mode === 'signin' ? undefined : 'Use 8 characters or more.'}>
                                            <div className='relative'>
                                                <TextInput
                                                    id='auth-password'
                                                    name='password'
                                                    type={showPassword ? 'text' : 'password'}
                                                    autoComplete={mode === 'signin' ? 'current-password' : 'new-password'}
                                                    required
                                                    minLength={8}
                                                    value={password}
                                                    onChange={event => setPassword(event.target.value)}
                                                    className='pr-12'
                                                />

                                                <button
                                                    type='button'
                                                    onClick={() => setShowPassword(value => !value)}
                                                    aria-label={showPassword ? 'Hide password' : 'Show password'}
                                                    className='absolute right-1.5 top-1/2 grid size-9 -translate-y-1/2 place-items-center rounded-full text-cream/55 hover:text-cream'
                                                >
                                                    {showPassword ? <EyeOff className='size-4' /> : <Eye className='size-4' />}
                                                </button>
                                            </div>
                                        </Field>
                                    )}

                                    {mode === 'signup' && plan !== 'Free' && (
                                        <p className='rounded-2xl bg-citron/10 px-4 py-3 text-xs leading-relaxed text-citron'>
                                            You chose {plan}. Your 14 free days begin today, with no card required.
                                        </p>
                                    )}

                                    {notice && <FormMessage tone='success'>{notice}</FormMessage>}
                                    {error && <FormMessage>{error}</FormMessage>}

                                    <PillButton type='submit' loading={busy} className='w-full'>
                                        {submitLabel}
                                    </PillButton>
                                </form>

                                <div className='mt-6 text-center text-sm'>
                                    {mode === 'signin' && (
                                        <button type='button' onClick={() => switchMode('forgot')} className='text-cream/60 underline decoration-cream/30 underline-offset-[6px] hover:text-cream'>
                                            Forgotten your password?
                                        </button>
                                    )}

                                    {(mode === 'forgot' || mode === 'reset') && (
                                        <div className='flex flex-col items-center gap-3'>
                                            {mode === 'reset' && (
                                                <button type='button' onClick={() => switchMode('forgot')} className='text-cream/60 underline decoration-cream/30 underline-offset-[6px] hover:text-cream'>
                                                    Send me another code
                                                </button>
                                            )}

                                            <button type='button' onClick={() => switchMode('signin')} className='text-cream/60 underline decoration-cream/30 underline-offset-[6px] hover:text-cream'>
                                                Return to sign in
                                            </button>
                                        </div>
                                    )}

                                    {mode === 'signup' && (
                                        <p className='text-xs leading-relaxed text-cream/45'>
                                            Opening an account means you accept our{' '}
                                            <Link href='/terms' className='underline decoration-cream/30 underline-offset-2 hover:text-cream'>
                                                terms
                                            </Link>
                                            {' '}and{' '}
                                            <Link href='/privacy' className='underline decoration-cream/30 underline-offset-2 hover:text-cream'>
                                                privacy policy
                                            </Link>
                                            .
                                        </p>
                                    )}
                                </div>
                            </>
                        )}
                </div>
            </section>
        </Layout>
    )
}

export default LoginPage
